import type { $Fetch } from "nitropack";

export interface LinkedInProfile {
    name?: string;
    headline?: string;
    location?: string;
    about?: string;
    profile_url?: string;
    profile_picture_url?: string;
}

export interface LinkedInExperience {
    title?: string;
    company?: string;
    company_url?: string;
    location?: string;
    start_date?: string;
    end_date?: string;
    description?: string;
    is_current?: boolean;
    confidence?: number;  // 0.0-1.0, reliability index from parser
    // Local-only fields (never returned by the scraper/parser): set client-side
    // when a long CDI is split into sub-periods via BSplitExperienceModal (#136).
    skill_ids?: string[];
    contract_type?: string;
}

export interface LinkedInEducation {
    school?: string;
    school_url?: string;
    degree?: string;
    field_of_study?: string;
    start_date?: string;
    end_date?: string;
    description?: string;
}

export interface ScrapeLinkedInRequest {
    url: string;
}

export interface ScrapeMetadata {
    main_page_html_length?: number;
    experiences_page_html_length?: number;
    experiences_page_accessible?: boolean;
    experiences_items_found?: number;
    experiences_parsed?: number;
    current_url_after_experiences?: string;
}

export interface ScrapeLinkedInResponse {
    success: boolean;
    error?: string;
    profile?: LinkedInProfile;
    experiences?: LinkedInExperience[];
    education?: LinkedInEducation[];
    skills?: string[];
    metadata?: ScrapeMetadata;
}

export interface ScraperStatusResponse {
    available: boolean;
    reason?: string;
    accounts_available?: number;
    accounts_total?: number;
    next_available_in_seconds?: number;
}

export const LinkedInRepository = (client: $Fetch) => ({
    /**
     * Check if the LinkedIn scraper is available.
     * Returns availability status without requiring authentication.
     */
    getStatus: (): Promise<ScraperStatusResponse> => {
        return client('/v2/linkedin/status', {
            method: 'GET',
        });
    },

    scrapeProfile: (data: ScrapeLinkedInRequest): Promise<ScrapeLinkedInResponse> => {
        return client('/v2/linkedin/scrape', {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                "Content-Type": "application/json",
            },
            // 5 minute timeout for LinkedIn scraping (login + scrape can take time)
            timeout: 300000,
        });
    },

    parsePdf: (file: File): Promise<ScrapeLinkedInResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        return client('/v2/linkedin/parse-pdf', {
            method: 'POST',
            body: formData,
            timeout: 30000,
        });
    },
});
